import { Alert, Box, Paper, Skeleton } from "@mui/material";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PidCanvas from "../components/PidCanvas";
import SelectedEquipmentPanel from "../components/SelectedEquipmentPanel";
import SystemPidPanel from "../components/SystemPidPanel";
import { usePidDetail, usePlantTree } from "../hooks/usePidExplorer";
import { useDashboardPlants } from "../hooks/usePlantOverview";
import PageHeader from "../layout/PageHeader";
import { spofCardHref } from "../utils/links";

/** How long the tag a visitor was deep-linked to keeps pulsing. */
const FOCUS_MS = 8000;

export default function PidExplorerPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const plants = useDashboardPlants().data ?? [];
  const plantId = params.get("plant") ?? plants[0]?.id ?? null;
  const tree = usePlantTree(plantId).data;
  // No drawing opens on its own: the explorer starts on the asset hierarchy.
  const pidId = params.get("pid");
  const pid = usePidDetail(plantId, pidId).data;
  const selectedTagId = params.get("kks");

  // A deep link only carries the drawing, so the tree unfolds the system holding it.
  const systemOfPid = tree?.find((system) =>
    system.pids.some((entry) => entry.id === pidId),
  );
  const systemId = params.get("system") ?? systemOfPid?.id ?? null;

  const selectedTag =
    pid?.tags.find((tag) => tag.kks_id === selectedTagId) ?? null;
  const plantName = plants.find((plant) => plant.id === plantId)?.name;

  function select(next: Record<string, string | null>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === null) merged.delete(key);
      else merged.set(key, value);
    }
    setParams(merged, { replace: true });
  }

  // Tag reached from a SPOF link: pulse it for a while, then fall back to a plain selection.
  const focused = params.get("focus") === "1" && selectedTagId !== null;

  useEffect(() => {
    if (!focused) return;
    const timer = setTimeout(() => {
      setParams(
        (current) => {
          const merged = new URLSearchParams(current);
          merged.delete("focus");
          return merged;
        },
        { replace: true },
      );
    }, FOCUS_MS);
    return () => clearTimeout(timer);
  }, [focused, setParams]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHeader
        title="Digital P&ID - SPOF & Dependency Explorer"
        trail={[
          { label: "Fleet overview", to: "/dashboard" },
          ...(plantId
            ? [
                {
                  label: plantName ?? "Plant",
                  to: `/dashboard/plants/${plantId}`,
                },
              ]
            : []),
          { label: "Digital P&ID Explorer" },
        ]}
      />

      {focused && (
        <Alert
          severity="info"
          onClose={() => select({ focus: null })}
          sx={{ mb: 2 }}
        >
          Showing <strong>{selectedTagId}</strong>
          {selectedTag?.spofs.length
            ? ` - matched occurrence of SPOF ${selectedTag.spofs.map((spof) => spof.spof_id).join(", ")}`
            : ""}
          , outlined in red on the drawing.
        </Alert>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", lg: "280px 1fr 320px" },
          // Panels keep their own scrollbar instead of stretching the page.
          gridAutoRows: { lg: "minmax(0, 1fr)" },
        }}
      >
        <SystemPidPanel
          systems={tree}
          selectedSystemId={systemId}
          selectedPidId={pidId}
          onSelectSystem={(id) =>
            select({ system: id, pid: null, kks: null, focus: null })
          }
          onSelectPid={(id) => select({ pid: id, kks: null, focus: null })}
        />

        {!pidId ? (
          <Paper
            variant="outlined"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            Pick a system, then a drawing, to explore its SPOF tags.
          </Paper>
        ) : !pid ? (
          <Skeleton variant="rounded" />
        ) : (
          <PidCanvas
            key={pid.id}
            plantId={plantId!}
            pid={pid}
            selectedTagId={selectedTagId}
            focused={focused}
            onSelectTag={(id) => select({ kks: id, focus: null })}
          />
        )}

        <SelectedEquipmentPanel
          tag={selectedTag}
          onClose={() => select({ kks: null })}
          onSelectKks={(kks) =>
            select({
              pid: kks.pid_id,
              kks: kks.kks_id,
              system: null,
              focus: null,
            })
          }
          onOpenSpof={(spofId) => navigate(spofCardHref(plantId!, spofId))}
        />
      </Box>
    </Box>
  );
}
